"use client"

/**
 * DeviceRow.jsx - Fleet Premium (OPTIMIZED v2)
 *
 * OTIMIZAÇÕES DE PERFORMANCE:
 * 1. makeStyles SEM parâmetros dinâmicos (statusColor via CSS variable)
 * 2. Estilos estáticos gerados uma única vez
 * 3. Cores aplicadas via style inline (--status-color)
 * 4. Menos hooks, menos recálculos
 */

import { memo, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import PlaceIcon from "@mui/icons-material/Place"
import NavigationIcon from "@mui/icons-material/Navigation"
import PauseIcon from "@mui/icons-material/Pause"
import StopIcon from "@mui/icons-material/Stop"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import VideocamIcon from "@mui/icons-material/Videocam"
import { formatSpeed } from "../common/util/formatter"
import { devicesActions } from "../store"
import { useTranslation } from "../common/components/LocalizationProvider"
import { getEffectiveStatus } from "../common/util/deviceColors"
import { deviceHasCamera } from "../common/util/cameras"
import { TelemetryBar, QuickActions } from "./components"

// ============================================
// CONSTANTES - Cores estáticas (evita recálculo)
// ============================================
const STATUS_COLORS = {
  moving: '#3b82f6',   // Azul
  online: '#22c55e',   // Verde
  stopped: '#22c55e',  // Verde
  offline: '#ef4444',  // Vermelho
  unknown: '#6b7280',  // Cinza
  idle: '#f59e0b',     // Âmbar
  alarm: '#ef4444',    // Vermelho
}

// ============================================
// HELPERS - Fora do componente (sem recriação)
// ============================================
const formatTimeAgo = (dateString) => {
  if (!dateString) return null
  const now = Date.now()
  const date = new Date(dateString).getTime()
  const diffSec = Math.floor((now - date) / 1000)
  if (diffSec < 60) return `${diffSec}s`
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m`
  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour}h`
  return `${Math.floor(diffHour / 24)}d`
}

const truncateAddress = (address, maxLen = 50) => {
  if (!address) return null
  return address.length > maxLen ? `${address.substring(0, maxLen - 3)}...` : address
}

const getStatusColor = (effectiveStatus, hasAlarm) => {
  if (hasAlarm) return STATUS_COLORS.alarm
  return STATUS_COLORS[effectiveStatus] || STATUS_COLORS.idle
}

// ============================================
// STYLES - ESTÁTICOS (sem parâmetros dinâmicos)
// Cores via CSS custom property --status-color
// ============================================
const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark'

  return {
    // Container principal
    row: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '10px 12px',
      margin: '4px 6px',
      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
      borderRadius: '10px',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      cursor: 'pointer',
      transition: 'background 0.15s ease, border-color 0.15s ease',
      position: 'relative',
      // Borda colorida via CSS variable
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        background: 'var(--status-color, #6b7280)',
        opacity: 0.6,
        borderRadius: '10px 0 0 10px',
        transition: 'opacity 0.15s ease',
      },
      '&:hover': {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        '&::before': { opacity: 1 },
      },
    },
    rowSelected: {
      background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
      borderColor: isDark ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.4)',
      '&::before': { opacity: 1, width: '4px' },
    },
    rowExpanded: {
      background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)',
      '&::before': { opacity: 1 },
    },

    // Status Indicator
    statusIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: '2px',
      marginRight: '10px',
    },
    statusIcon: {
      fontSize: '20px',
      color: 'var(--status-color, #6b7280)',
      transition: 'transform 0.2s ease',
    },
    statusIconMoving: {
      animation: '$iconPulse 2s ease-in-out infinite',
    },
    '@keyframes iconPulse': {
      '0%, 100%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.1)' },
    },

    // Content
    content: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    nameLine: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    },
    deviceName: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
    },
    deviceNameOffline: {
      opacity: 0.6,
    },
    speedBadge: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '2px 8px',
      background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      borderRadius: '4px',
      flexShrink: 0,
      color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)',
    },
    speedBadgeFast: {
      color: '#ef4444',
      background: 'rgba(239,68,68,0.15)',
    },
    addressLine: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
      fontSize: '0.8rem',
    },
    addressLineOffline: {
      opacity: 0.5,
    },
    addressIcon: {
      fontSize: '0.7rem',
      opacity: 0.7,
    },
    address: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
    },
    timeAgo: {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      fontSize: '0.7rem',
      flexShrink: 0,
      fontFamily: '"JetBrains Mono", monospace',
    },
    cameraBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '6px',
      padding: '1px 4px',
      borderRadius: '4px',
      background: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
      color: '#3b82f6',
      fontSize: '0.65rem',
      fontWeight: 600,
      flexShrink: 0,
    },
    cameraIcon: {
      fontSize: '12px',
    },
    expandedContent: {
      marginTop: '6px',
      paddingTop: '6px',
      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
  }
})

// ============================================
// COMPARADOR OTIMIZADO
// ============================================
const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.device !== nextProps.device) return false
  if (prevProps.isSelected !== nextProps.isSelected) return false

  const wasExpanded = prevProps.expandedId === prevProps.device?.id
  const isExpanded = nextProps.expandedId === nextProps.device?.id
  if (wasExpanded !== isExpanded) return false

  // Colapsada: só verifica dados visíveis
  if (!isExpanded) {
    const prevPos = prevProps.position
    const nextPos = nextProps.position
    if (!prevPos && !nextPos) return true
    if (!prevPos || !nextPos) return false
    return (
      prevPos.speed === nextPos.speed &&
      prevPos.address === nextPos.address &&
      prevPos.attributes?.motion === nextPos.attributes?.motion &&
      prevPos.attributes?.alarm === nextPos.attributes?.alarm
    )
  }

  // Expandida: qualquer mudança na position
  return prevProps.position === nextProps.position
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const DeviceRow = memo(({ device, position, expandedId, onToggleExpand, isSelected }) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const t = useTranslation()

  if (!device) return null

  const isExpanded = expandedId === device.id

  // Cálculos simples - sem useMemo excessivo
  const effectiveStatus = getEffectiveStatus(
    device.status,
    position?.attributes?.motion,
    !!position
  )
  const hasAlarm = !!position?.attributes?.alarm
  const statusColor = getStatusColor(effectiveStatus, hasAlarm)

  const speed = position?.speed ? Math.round(position.speed * 1.852) : 0
  const course = position?.course || 0
  const address = truncateAddress(position?.address)
  const timeAgo = formatTimeAgo(position?.fixTime || device.lastUpdate)
  const isOffline = effectiveStatus === 'offline' || effectiveStatus === 'unknown'
  const isMoving = effectiveStatus === 'moving'
  const hasCamera = deviceHasCamera(device, position)

  // Handler simplificado
  const handleClick = useCallback(() => {
    dispatch(devicesActions.selectId(device.id))
    if (onToggleExpand) {
      onToggleExpand(isExpanded ? null : device.id)
    }
  }, [dispatch, device.id, onToggleExpand, isExpanded])

  const handleQuickAction = useCallback(() => {
    if (onToggleExpand) onToggleExpand(null)
  }, [onToggleExpand])

  // Ícone de status baseado no estado
  const StatusIcon = useMemo(() => {
    if (isMoving || hasAlarm) {
      return (
        <NavigationIcon
          className={cx(classes.statusIcon, isMoving && classes.statusIconMoving)}
          style={{ transform: `rotate(${course}deg)` }}
        />
      )
    }
    if (effectiveStatus === 'offline') {
      return <StopIcon className={classes.statusIcon} />
    }
    if (effectiveStatus === 'unknown') {
      return <RadioButtonUncheckedIcon className={classes.statusIcon} />
    }
    return <PauseIcon className={classes.statusIcon} />
  }, [isMoving, hasAlarm, effectiveStatus, course, classes, cx])

  return (
    <Box
      className={cx(
        classes.row,
        isSelected && classes.rowSelected,
        isExpanded && classes.rowExpanded
      )}
      style={{ '--status-color': statusColor }}
      onClick={handleClick}
    >
      {/* Status Indicator */}
      <Box className={classes.statusIndicator}>
        {StatusIcon}
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {/* Line 1: Name + Speed */}
        <Box className={classes.nameLine}>
          <Typography
            className={cx(classes.deviceName, isOffline && classes.deviceNameOffline)}
            component="span"
          >
            {hasAlarm && '🚨 '}{device.name}
            {hasCamera && (
              <span className={classes.cameraBadge}>
                <VideocamIcon className={classes.cameraIcon} />
              </span>
            )}
          </Typography>
          {speed > 0 && (
            <span className={cx(classes.speedBadge, speed > 80 && classes.speedBadgeFast)}>
              {speed} km/h
            </span>
          )}
        </Box>

        {/* Line 2: Address + Time */}
        <Box className={cx(classes.addressLine, isOffline && classes.addressLineOffline)}>
          <PlaceIcon className={classes.addressIcon} />
          <span className={classes.address}>
            {address || t('sharedNoAddress') || 'Endereço não disponível'}
          </span>
          {timeAgo && <span className={classes.timeAgo}>{timeAgo}</span>}
        </Box>

        {/* Expanded Content - Lazy render */}
        {isExpanded && (
          <Box className={classes.expandedContent}>
            <TelemetryBar position={position} device={device} />
            <QuickActions
              deviceId={device.id}
              deviceName={device.name}
              position={position}
              onAction={handleQuickAction}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}, arePropsEqual)

DeviceRow.displayName = "DeviceRow"

export default DeviceRow
